/**
 * Pagina publica de captura cliente (Sprint T2.2).
 *
 * Ruta `/capture/:token`. Sin sesion ni auth context — la autoridad
 * la da el token. Render dinamico de la plantilla cliente con
 * SurveyRenderer; al completar el cliente envia las respuestas y la
 * pagina queda en estado "submitted".
 *
 * Diferencia con Portal.tsx (read-only): Capture es write-once. Una
 * vez que el cliente envia, el token queda marcado y no puede volver
 * a enviar.
 */
import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { type SurveyTemplate, SurveyTemplateSchema } from '@assessos/shared';

import {
  type CaptureViewPublic,
  fetchCaptureView,
  submitCaptureResponses,
} from '../api/capture.js';
import { SurveyRenderer } from '../components/SurveyRenderer.js';

type ErrorKind =
  | 'not_found'
  | 'expired'
  | 'revoked'
  | 'rate_limited'
  | 'schema_invalid'
  | 'unknown';

function classifyError(err: unknown): ErrorKind {
  const msg = err instanceof Error ? err.message : '';
  if (msg.startsWith('401:')) return 'not_found';
  if (msg.startsWith('410:')) {
    if (msg.includes('expired')) return 'expired';
    if (msg.includes('revoked')) return 'revoked';
    return 'expired';
  }
  if (msg.startsWith('429:')) return 'rate_limited';
  return 'unknown';
}

export function CapturePage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<CaptureViewPublic | null>(null);
  const [schema, setSchema] = useState<SurveyTemplate | null>(null);
  const [responses, setResponses] = useState<Record<string, unknown>>({});
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetchCaptureView(token)
      .then((v) => {
        setView(v);
        const parsed = SurveyTemplateSchema.safeParse(v.template.schema);
        if (parsed.success) {
          setSchema(parsed.data);
          if (v.already_submitted) setSubmitted(true);
        } else {
          setErrorKind('schema_invalid');
        }
      })
      .catch((err: unknown) => setErrorKind(classifyError(err)));
  }, [token]);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!token) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await submitCaptureResponses(token, responses);
      // ADR-041 D2: si el backend calculo snapshot redirige a la vista de resultados.
      if (result.hasResult) {
        navigate(`/capture/${encodeURIComponent(token)}/resultado`);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'error';
      if (msg.startsWith('409:already_submitted')) {
        setSubmitted(true);
      } else if (msg.startsWith('410:')) {
        setErrorKind('expired');
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (errorKind) return <CaptureError kind={errorKind} />;
  if (!view || !schema) return <CaptureLoading />;
  if (submitted) return <CaptureThanks clientName={view.site.client_name} />;

  return (
    <main className="min-h-screen bg-slate-50">
      <header className="bg-brand-700 text-white px-6 py-8">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 font-bold">
              AF
            </span>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/70">
                Formulario previo · MSSPRD
              </p>
              <h1 className="text-xl font-semibold">{view.site.client_name}</h1>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/85">
            {view.template.name} · sitio: {view.site.name}
          </p>
          <p className="mt-1 text-xs text-white/70">
            Por favor complete este formulario antes de la visita del auditor. Sus respuestas
            quedaran asociadas al sitio y serviran como contexto previo a la evaluacion.
          </p>
        </div>
      </header>

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="mx-auto max-w-3xl px-4 py-6 space-y-6"
      >
        <SurveyRenderer schema={schema} responses={responses} onChange={setResponses} />

        {submitError && (
          <p role="alert" className="text-sm text-red-700">
            Error al enviar: {submitError}. Intenta nuevamente.
          </p>
        )}

        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800">
          Una vez que envies este formulario no podras modificarlo. Verifica que las respuestas
          reflejen lo que conoces del sitio.
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-brand-700 px-5 py-3 text-sm font-semibold text-white disabled:bg-slate-300"
          >
            {submitting ? 'Enviando...' : 'Enviar formulario'}
          </button>
        </div>

        <footer className="text-center text-xs text-slate-400 pt-4">
          Cumple · Sus respuestas se transmiten cifradas (HTTPS) y se almacenan asociadas al sitio.
          Solo el equipo auditor las ve.
        </footer>
      </form>
    </main>
  );
}

function CaptureLoading(): JSX.Element {
  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-sm text-slate-500">Cargando formulario...</p>
    </main>
  );
}

function CaptureError({ kind }: { kind: ErrorKind }): JSX.Element {
  const titles: Record<ErrorKind, string> = {
    not_found: 'Enlace invalido',
    expired: 'Enlace expirado',
    revoked: 'Enlace revocado',
    rate_limited: 'Demasiados intentos',
    schema_invalid: 'Plantilla incompatible',
    unknown: 'No se pudo cargar el formulario',
  };
  const descriptions: Record<ErrorKind, string> = {
    not_found:
      'El enlace no es valido. Verifica que copiaste la URL completa o solicita uno nuevo a tu contacto MSSPRD.',
    expired: 'El enlace expiro. Solicita un nuevo enlace a tu contacto MSSPRD.',
    revoked: 'El enlace fue revocado. Contacta a tu auditor MSSPRD.',
    rate_limited:
      'Demasiados intentos consecutivos. Espera unos minutos antes de volver a intentar.',
    schema_invalid: 'La plantilla asociada al enlace no se pudo cargar. Contacta a MSSPRD.',
    unknown: 'Ocurrio un error inesperado. Intenta mas tarde o contacta a MSSPRD.',
  };
  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-700 text-white font-bold mb-4">
          C
        </div>
        <h1 className="text-xl font-semibold text-slate-900">{titles[kind]}</h1>
        <p className="mt-3 text-sm text-slate-600">{descriptions[kind]}</p>
        <p className="mt-6 text-xs text-slate-400">Cumple · un producto de MSSPRD</p>
      </div>
    </main>
  );
}

function CaptureThanks({ clientName }: { clientName: string }): JSX.Element {
  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-green-600 text-white font-bold mb-4">
          ok
        </div>
        <h1 className="text-xl font-semibold text-slate-900">Formulario enviado</h1>
        <p className="mt-3 text-sm text-slate-600">
          Gracias por completar el formulario previo, {clientName}. El equipo MSSPRD lo revisara
          antes de la visita del auditor.
        </p>
        <p className="mt-6 text-xs text-slate-400">
          Este enlace ya no puede usarse para enviar respuestas adicionales. Si necesitas corregir
          algo, contacta a tu auditor.
        </p>
      </div>
    </main>
  );
}
